import React from "react";
import * as MUI from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ConfirmAndCancelButtons from "../../../../components/buttons/ConfirmAndCancelButtons";
import { mandatory } from "../../../../utils/Functions";

const NAME_MAX_LENGTH = 120;
const ADDRESS_MAX_LENGTH = 255;
const PHONE_NUMBER_MAX_LENGTH = 30;

export default function InstitutionFormDialog({ title, initialValues, isSubmitting, onSubmit, onClose }) {
  const { t } = useTranslation();
  const theme = MUI.useTheme();
  const fullScreen = MUI.useMediaQuery(theme.breakpoints.down("sm"));
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: initialValues?.name ?? "",
      address: initialValues?.address ?? "",
      phoneNumber: initialValues?.phoneNumber ?? "",
    },
  });

  const submitData = (data) => {
    onSubmit({
      name: data.name.trim(),
      address: data.address?.trim() || null,
      phoneNumber: data.phoneNumber?.trim() || null,
    });
  };

  return (
    <MUI.Dialog
      open
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}
    >
      <MUI.DialogTitle sx={{ p: { xs: 2.5, sm: 3 }, pr: 7 }}>
        <MUI.Typography variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </MUI.Typography>
        <MUI.IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 16, right: 16 }}
        >
          <CloseIcon fontSize="small" />
        </MUI.IconButton>
      </MUI.DialogTitle>
      <form onSubmit={handleSubmit(submitData)}>
        <MUI.DialogContent sx={{ px: { xs: 2.5, sm: 3 }, pb: 1 }}>
          <MUI.Stack spacing={2}>
            <Controller
              control={control}
              name="name"
              rules={{
                required: t("EmptyField"),
                maxLength: { value: NAME_MAX_LENGTH, message: t("MaxCharacters") + `: ${NAME_MAX_LENGTH}` },
              }}
              render={({ field }) => (
                <MUI.TextField
                  {...field}
                  autoFocus
                  label={mandatory(t("Name"))}
                  placeholder={t("InstitutionNamePlaceholder")}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: NAME_MAX_LENGTH }}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="address"
              render={({ field }) => (
                <MUI.TextField
                  {...field}
                  label={t("Address")}
                  placeholder={t("AddressPlaceholder")}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: ADDRESS_MAX_LENGTH }}
                />
              )}
            />
            <Controller
              control={control}
              name="phoneNumber"
              render={({ field }) => (
                <MUI.TextField
                  {...field}
                  label={t("PhoneNumber")}
                  placeholder={t("PhoneNumberPlaceholder")}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: PHONE_NUMBER_MAX_LENGTH }}
                />
              )}
            />
          </MUI.Stack>
        </MUI.DialogContent>
        <MUI.DialogActions sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5 }}>
          <ConfirmAndCancelButtons
            confirmTitle="Save"
            cancelTitle=""
            onCancel={onClose}
            confirmButtonType="submit"
            isConfirmLoading={isSubmitting}
          />
        </MUI.DialogActions>
      </form>
    </MUI.Dialog>
  );
}
